import React, { useEffect } from 'react';
import { message, notification } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/Loader/Loader';

import { CONNECT_HUBSPOT } from 'graphql/queries/integrationQueries';
import {
  CREATE_HUBSPOT_AUTH_URL,
  CREATE_HUBSPOT_INSTALL_STATE,
} from 'graphql/mutations/integrationMutation';
import { fromAppVar, hubspotUrlParams, isLoggedInVar, userVar } from 'graphql/cache';

// Marketplace flow: HubSpot must get the user back on returnUrl after finalize
function getFinalizeReturnUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('step') === 'finalize' ? params.get('returnUrl') : null;
}

const HubSpotWindow = ({ history }) => {
  const [connectHubSpot] = useLazyQuery(CONNECT_HUBSPOT, {
    variables: { url: window.location.href },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.connectHubspot?.state) {
        hubspotUrlParams(data.connectHubspot.state);
        fromAppVar('hubspot');
      }

      if (data?.connectHubspot?.token) {
        window.localStorage.setItem('prospero-token', data.connectHubspot.token);
        window.localStorage.setItem('prospero-user-id', data.connectHubspot.user._id);

        isLoggedInVar(true);
        userVar({ ...data.connectHubspot.user });
      }

      const finalizeReturnUrl = getFinalizeReturnUrl();
      if (finalizeReturnUrl) {
        window.location.href = finalizeReturnUrl;
        return;
      }

      if (data?.connectHubspot?.token) {
        history.push('/dashboard');
        window.close();
      } else {
        message.success('hubspot Connected Successfully');
        history.push('/integration?hubspot=connected');
        window.close();
      }
    },

    onError: (err) => {
      console.log('error', err);
      if (err.message === 'HubSpot account already exists') {
        message.error('HubSpot account already connected to another account');
      } else if (err.message === 'User already exists') {
        message.error('User already exists');
      } else {
        message.error('HubSpot Connect Failed');
      }
      err.graphQLErrors.forEach((err) => {
        if (err.extensions.state) {
          hubspotUrlParams(err.extensions.state);
          fromAppVar('hubspot');
        }
      });
      setTimeout(() => history.push('/integration?hubspot=failed'), 1000);
    },
  });

  const [createHubspotAuthUrl] = useMutation(CREATE_HUBSPOT_AUTH_URL, {
    variables: {
      state: JSON.stringify(hubspotUrlParams()),
    },
    onCompleted: async (data) => {
      const authData = data.createHubspotAuthUrl;
      window.open(authData.url, '_self');
    },
    onError: (err) => {
      notification.error({
        message: 'Hubspot Create Auth URL Failed',
        description: 'Something went wrong',
      });
      history.push('/signup');
    },
  });

  const [createHubspotInstallState] = useMutation(CREATE_HUBSPOT_INSTALL_STATE, {
    onCompleted: (data) => {
      const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');

      if (!data?.createHubspotInstallState || !returnUrl) {
        message.error('HubSpot Connect Failed');
        history.push('/integration?hubspot=failed');
        return;
      }

      const redirectUrl = new URL(returnUrl);
      redirectUrl.searchParams.set('state', data.createHubspotInstallState);
      window.location.href = redirectUrl.toString();
    },
    onError: () => {
      message.error('HubSpot Connect Failed');
      history.push('/integration?hubspot=failed');
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const step = params.get('step');
    const code = params.get('code');

    // Marketplace secure install flow
    if (step === 'authorize') {
      const isLoggedIn = isLoggedInVar() || !!window.localStorage.getItem('prospero-token');

      if (!isLoggedIn) {
        // Carry this request on the login redirect, so we resume it afterwards
        // without needing storage HubSpot's iframe may not share with us.
        const resumeUrl = `/hubspot${window.location.search}`;
        history.push(`/login?redirect_url=${encodeURIComponent(resumeUrl)}`);
        return;
      }

      // The state token is minted and validated by the backend — the install page
      // runs framed by HubSpot, where storage is partitioned and anything we keep
      // client-side is unreadable by the time HubSpot finalizes the install.
      createHubspotInstallState();
      return;
    }

    if (step === 'finalize' && code) {
      // connectHubspot validates the state token before exchanging the code
      connectHubSpot();
      return;
    }

    // Original flow (install from within Prospero, not via Marketplace)
    if (code) {
      connectHubSpot();
    } else {
      createHubspotAuthUrl();
    }
  }, [connectHubSpot, createHubspotAuthUrl, createHubspotInstallState, history]);

  return <Loader />;
};

export default HubSpotWindow;
